// const dynamicKeys = dataLoaded.length > 0 ? Object.keys(dataLoaded[0]) : [];
//   const columnsGen = dynamicKeys.map((key) => ({
//     accessorKey: key,
//     header: key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()),
//     cell: (info) => info.getValue(),
//   }));
//   setColumnsGenerated(columnsGen);
// <CustomTable data={dataLoaded} columns={columnsGenerated} />
import React, { useState, useEffect } from 'react';
import { 
  useReactTable, getCoreRowModel, getFilteredRowModel, 
  getPaginationRowModel, getSortedRowModel, flexRender 
} from '@tanstack/react-table';
import { 
  Table, TableHead, TableBody, TableCell, TableRow, 
  TableContainer, TableFooter, TableSortLabel, Paper, 
  TextField, Typography, Box, TablePagination 
} from '@mui/material';
import { grey } from '@mui/material/colors';
import CopyAll from '@mui/icons-material/CopyAll';

// ---- Filter Component
function Filter({ column, table }) {
  const firstValue = table.getPreFilteredRowModel().flatRows[0]?.getValue(column.id);
  const columnFilterValue = column.getFilterValue();
  
  return typeof firstValue === 'number' ? (
    <TextField 
      type="number" 
      size="small" 
      value={columnFilterValue ?? ''} 
      onChange={(e) => column.setFilterValue(e.target.value ? Number(e.target.value) : undefined)} 
      placeholder="Filter..." 
    />
  ) : (
    <TextField 
      size="small" 
      value={columnFilterValue ?? ''} 
      onChange={(e) => column.setFilterValue(e.target.value)} 
      placeholder="Search..." 
    />
  );
}

export default function CustomTable({ data = [], columns = [], showFilter = true, showSearchAllColumn = true, height = 'auto' }) {
  // const [data] = useState(() => [...initialData]);
  // const [columns] = useState(() => [...initialColumns]);
  const [sorting, setSorting] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 20, // Thay đổi mặc định tại đây
  });

  const table = useReactTable({
    data,
    columns,
    state: { sorting, globalFilter, pagination },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  // Example dynamic calculation for 'age' column if it exists
  const hasAgeColumn = columns.some((col) => col.accessorKey === 'age');
  const ageSum = hasAgeColumn
    ? table.getFilteredRowModel().rows.reduce((sum, row) => sum + (row.original.age || 0), 0)
    : 0;

  useEffect(() => {
    // handleGridHeight(); if defined elsewhere
  }, []);

  const copyColumnToClipboard = (columnId) => {
  // 1. Lấy toàn bộ data hiện tại của bảng (ví dụ mảng data nguồn của bạn là 'tableData')
  // Hoặc nếu dùng thư viện có sẵn, bạn lấy qua table.getRowModel().rows
  const columnData = data.map(row => row[columnId] || ''); 

  // 2. Nối các hàng lại với nhau bằng dấu xuống dòng (\n) để dán vào Excel thành 1 cột
  const textToCopy = columnData.join('\n');

  // 3. Thực hiện copy vào bộ nhớ đệm
  if (navigator.clipboard) {
    navigator.clipboard.writeText(textToCopy)
      // .then(() => alert('Đã copy toàn bộ cột vào clipboard để dán Excel!'))
      .catch(err => console.error('Lỗi copy:', err));
  } else {
    alert('Trình duyệt không hỗ trợ Clipboard API');
  }
};

  const allColumnsCount = table.getAllLeafColumns().length;

  return (
    <Box sx={{ p: 0 }}>
      {showSearchAllColumn?
      <TextField 
        fullWidth 
        size="small" 
        value={globalFilter ?? ''} 
        onChange={(e) => setGlobalFilter(e.target.value)} 
        placeholder="Search all columns..." 
        sx={{ mb: 2 }} 
      />:''}
      <TableContainer component={Paper} style={{height: height}}>
        <Table stickyHeader>
          <TableHead>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header, idx, arr) => (
                  <TableCell 
                    key={header.id} 
                    sx={{ borderRight: idx < arr.length - 1 ? '1px solid #ddd' : 'none', fontWeight: 'bold', backgroundColor: grey[200]
                      , padding: 1, textAlign: header.column.columnDef.align }}
                  >
                    {header.isPlaceholder ? null : (
                      <TableSortLabel 
                        active={!!header.column.getIsSorted()} 
                        direction={header.column.getIsSorted() || 'asc'} 
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </TableSortLabel>
                    )}
                    {header.column.columnDef.copyColumn?
                    <CopyAll style={{position: 'absolute', right: 0, top: 0, fontSize: '20px', cursor: 'pointer'}}
                      onClick={()=>copyColumnToClipboard(header.column.id)}/>:''}
                  </TableCell>
                ))}
              </TableRow>
            ))}
            {/* Filters Row */
            showFilter?
            <TableRow>
              {table.getHeaderGroups()[0].headers.map((header, idx, arr) => (
                <TableCell key={header.id} sx={{ borderRight: idx < arr.length - 1 ? '1px solid #ddd' : 'none' }}>
                  {header.column.getCanFilter() ? <Filter column={header.column} table={table} /> : null}
                </TableCell>
              ))}
            </TableRow>
            :''}
          </TableHead>
          
          <TableBody>
            {table.getRowModel().rows.map((row, rowIndex) => (
              <TableRow 
                key={row.id} 
                sx={{ backgroundColor: rowIndex % 2 === 0 ? grey[150] : 'white', '&:hover': { backgroundColor: grey[200] } }}
              >
                {row.getVisibleCells().map((cell, idx, arr) => (
                  <TableCell key={cell.id} sx={{ borderRight: idx < arr.length - 1 ? '1px solid #ddd' : 'none'
                  , padding: 1, textAlign: cell.column.columnDef.align }}>
                    {cell.column.columnDef.isNumeric ? (
                      // Nếu là cột số (đã căn phải ở bước trước), lấy giá trị thực tế ra format trực tiếp thành chuỗi
                      cell.getValue() !== null && cell.getValue() !== undefined && cell.getValue() !== '' ? (
                        new Intl.NumberFormat('en-US', { 
                          minimumFractionDigits: 2, 
                          maximumFractionDigits: 2 
                        }).format(Number(cell.getValue()))
                      ) : ''
                    ) : (
                      // Nếu là cột chữ bình thường, để flexRender tự xử lý cấu hình gốc
                      flexRender(cell.column.columnDef.cell, cell.getContext())
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>

          {/* ---- DYNAMIC FOOTER ---- */}
          {hasAgeColumn && (
            <TableFooter>
              <TableRow sx={{ backgroundColor: grey[200] }}>
                <TableCell colSpan={allColumnsCount - 1} sx={{ fontWeight: 'bold', borderRight: '1px solid #ddd' }}>
                  Total Age
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>{ageSum}</TableCell>
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </TableContainer>

      <TablePagination 
        component="div" 
        count={table.getFilteredRowModel().rows.length} 
        page={table.getState().pagination.pageIndex} 
        rowsPerPage={table.getState().pagination.pageSize} 
        onPageChange={(e, newPage) => table.setPageIndex(newPage)} 
        onRowsPerPageChange={(e) => table.setPageSize(Number(e.target.value))} 
        rowsPerPageOptions={[20, 50, 100]} 
      />
    </Box>
  );
}